import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';

/* A Fabrica project's contract, read and signed by the customer's admin (/contract/<eventId>).
   The mail "Read and sign the agreement" signs them in and lands here. The text is the copy our
   team SENT (never our working copy), and the signature carries that copy's sha256, so a resend
   while this page is open refuses the signature instead of signing words nobody showed them.
   The HTML is cleaned server-side (shared_models mutations/project_contract.clean_html). */

const contractStyle = `
  .project-contract { text-align: left; font-size: 14px; line-height: 1.55; }
  .project-contract h1 { font-size: 22px; margin: 8px 0 12px; }
  .project-contract h2 { font-size: 18px; margin: 20px 0 8px; }
  .project-contract h3 { font-size: 15px; margin: 16px 0 6px; }
  .project-contract .table-wrap { overflow-x: auto; }
  .project-contract table { border-collapse: collapse; width: 100%; margin-bottom: 12px; font-size: 13px; }
  .project-contract th, .project-contract td { border: 1px solid #ccc; padding: 4px 6px; vertical-align: top; }
  .project-contract th { background: #f4f4f4; }
`;

// Wide tables scroll inside their own box instead of widening the whole page on a phone.
const wrapTables = (html) => (html || '')
  .replace(/<table/g, '<div class="table-wrap"><table')
  .replace(/<\/table>/g, '</table></div>');

const SignForm = ({ contract, eventId, onSigned }) => {
  const { t } = useTranslation();
  const [typedName, setTypedName] = useState('');
  const [title, setTitle] = useState('');
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const ready = typedName.trim().length >= 3 && title.trim() && accept && !busy;

  const sign = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const data = await apiRequest('POST', `/contract/${eventId}/sign`, {
        typed_name: typedName, title, accept, sha256: contract.sha256,
      });
      onSigned(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={sign} className="text-start border rounded p-3 mt-4 mb-5 bg-light">
      <h5 className="mb-3">{t('contractSignHeading')}</h5>
      <div className="mb-2">
        <label className="form-label small mb-1" htmlFor="contract-name">{t('contractFullName')}</label>
        <input id="contract-name" className="form-control" value={typedName} autoComplete="name"
               onChange={(e) => setTypedName(e.target.value)} maxLength={120} />
      </div>
      <div className="mb-3">
        <label className="form-label small mb-1" htmlFor="contract-title">{t('contractJobTitle')}</label>
        <input id="contract-title" className="form-control" value={title} autoComplete="organization-title"
               onChange={(e) => setTitle(e.target.value)} maxLength={120} />
      </div>
      <div className="form-check mb-3">
        <input id="contract-accept" type="checkbox" className="form-check-input" checked={accept}
               onChange={(e) => setAccept(e.target.checked)} />
        <label className="form-check-label small" htmlFor="contract-accept">
          {t('contractAcceptLabel', { company: contract.company_name || '' })}
        </label>
      </div>
      {error && <div className="small text-danger mb-2">{t(error)}</div>}
      <button type="submit" className="btn btn-primary w-100" disabled={!ready}>
        {busy ? t('contractSigning') : t('contractSignButton')}
      </button>
    </form>
  );
};

const ProjectContract = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiRequest('GET', `/contract/${eventId}`)
      .then(setContract)
      .catch((e) => setError(e.message));
  }, [eventId]);

  const openPdf = async () => {
    try {
      const data = await apiRequest('GET', `/contract/${eventId}/pdf`);
      if (data?.url) window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      setError(e.message);
    }
  };

  const signed = contract?.state === 'signed';
  return (
    <Screen color="white">
      <style>{contractStyle}</style>
      <div className="top w-100 px-3">
        <Title>{t('contractTitle')}</Title>
        {error && !contract && (
          <div className="alert alert-warning text-start">{t(error)}</div>
        )}
        {contract && (
          <>
            <div className="small text-muted mb-3">
              {contract.project_name}{contract.company_name ? ` · ${contract.company_name}` : ''}
            </div>
            {signed && (
              <div className="alert alert-success text-start d-flex align-items-center gap-2">
                <Icon name="check-circle" theme="success" />
                <div className="flex-grow-1">
                  {contract.uploaded
                    ? t('contractSignedCopy', { date: (contract.uploaded.at || '').slice(0, 10) })
                    : t('contractSignedBy', {
                      name: contract.signed.typed_name,
                      title: contract.signed.title,
                      date: (contract.signed.at || '').slice(0, 10),
                    })}
                </div>
                {contract.has_pdf && (
                  <button type="button" className="btn btn-sm btn-success" onClick={openPdf}>
                    {t('contractDownloadPdf')}
                  </button>
                )}
              </div>
            )}
            {error && <div className="small text-danger mb-2">{t(error)}</div>}
            {contract.html && (
              <div className="project-contract" dangerouslySetInnerHTML={{ __html: wrapTables(contract.html) }} />
            )}
            {!signed && <SignForm contract={contract} eventId={eventId} onSigned={setContract} />}
          </>
        )}
      </div>
    </Screen>
  );
};

export default ProjectContract;
